export const resolvePromiseWithDelay = <T extends any>(
  value: T,
  delay = 0,
): Promise<T> =>
  new Promise((res) => {
    setTimeout(() => res(value), delay);
  });

export const resolveSettled = async <T>(
  input:
    | Promise<PromiseSettledResult<Awaited<T>>[]>
    | PromiseSettledResult<Awaited<T> | T>[],
): Promise<{
  fulfilled: T[];
  rejected: any[];
}> => {
  const fulfilled: T[] = [];
  const rejected: any[] = [];

  const items = await input;
  for (const item of items) {
    if (item.status === "fulfilled") fulfilled.push(item.value);
    if (item.status === "rejected") rejected.push(item.reason);
  }

  return {
    fulfilled,
    rejected,
  };
};

export const waitSettledThenThrow = async <T>(
  promises: Promise<T>[],
): Promise<T[]> => {
  const { fulfilled, rejected } = await resolveSettled(
    Promise.allSettled(promises),
  );

  if (rejected.length) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw rejected;
  } else {
    return fulfilled;
  }
};

/**
 * Same error-collect-then-throw contract as waitSettledThenThrow, but runs
 * runners one at a time instead of in parallel. All runners are always executed
 * even if earlier ones fail.
 */
export const runSequentiallyThenThrow = async <T>(
  runners: (() => Promise<T>)[],
): Promise<T[]> => {
  const settled: PromiseSettledResult<T>[] = [];
  for (const runner of runners) {
    try {
      settled.push({ status: "fulfilled", value: await runner() });
    } catch (reason) {
      settled.push({ status: "rejected", reason });
    }
  }

  const { fulfilled, rejected } = await resolveSettled(settled);
  if (rejected.length) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw rejected;
  }
  return fulfilled;
};

/**
 * Runs promises sequentially but still returns all results.
 * Similar to Promise.allSettled, but executes promises one after another
 * instead of in parallel. This ensures all promises are executed even if
 * some of them fail.
 */
export const allSettledSequentially = async (
  promises: (() => Promise<any>)[],
): Promise<Array<PromiseSettledResult<any>>> => {
  const results: Array<PromiseSettledResult<any>> = [];

  for (const promiseFn of promises) {
    try {
      const value = await promiseFn();
      results.push({ status: "fulfilled", value });
    } catch (reason) {
      results.push({ status: "rejected", reason });
    }
  }

  return results;
};
